"use client";

// SNOWCLUB-STAGING-PAGE-LATENCY-001 · NATIVE RENDER — THE COMPILED DESIGN AS ONE COMPONENT, SERVER-RENDERED AND HYDRATED.
// Statically imports the build-time compiled design (AST + pseudo CSS + logic module) and the dc-runtime module, so they
// travel as one content-hashed application chunk (DesignSurface loads this through next/dynamic). The same component renders
// under react-dom/server — the generated modules shadow `window`/`document` with per-request stand-ins there — and in the
// browser, where the first render is identical (the viewport is pinned to the server-chosen width until measured after mount).
import * as React from "react";
import * as ReactDOMNamespace from "react-dom";
import { createRoot, hydrateRoot } from "react-dom/client";
import { DESIGN_AST, DESIGN_CONTRACT, DESIGN_PSEUDO_CSS, DESIGN_PROPS, DESIGN_DOC_MODE, createDesignLogic } from "@/generated/design-compiled";
import { createDesignApp, StreamableLogic } from "@/generated/dc-runtime";
import FeedbackForm from "@/components/landing/FeedbackForm";
import { patchContactSurface } from "@/components/site/design-contact-patch";
import type { AstNode } from "@/components/site/design-contact-patch";
import { applyVendorsDoor, STAGING_VENDORS_DOOR } from "@/lib/vendors-door";

// ENG-SOT-050 — the Kit's dummy /contact-us form is swapped, in its own slot, for the tested FeedbackForm (see the
// patch's header). Computed once per module; the generated AST export itself is never mutated.
const PATCHED_AST = applyVendorsDoor(patchContactSurface(DESIGN_AST as unknown as AstNode[], FeedbackForm), STAGING_VENDORS_DOOR);

// SAME-SITE NAVIGATION (contract-reachability / crawler-equivalence): the design addresses www.snowbus.com absolutely in
// its template and logic. Every href whose path the application contract serves is rendered root-relative, so the
// served document's internal links stay on the host they came from (staging or production). A path the contract does
// not serve keeps its absolute address; canonicals and the sitemap are not anchors and are untouched.
const CONTRACT = new Set<string>(DESIGN_CONTRACT);
const ABSOLUTE_SITE = /^https?:\/\/(?:www\.)?snowbus\.com(\/[^?#]*)?([?#].*)?$/i;
export function sameSiteHref(href: string): string {
  const m = ABSOLUTE_SITE.exec(href);
  if (!m) return href;
  const path = (m[1] ?? "/").replace(/\/+$/, "") || "/";
  return CONTRACT.has(path) ? path + (m[2] ?? "") : href;
}

/** The per-request facts the design reads at construction and first render (see serverWindow() in design-compiled.js). */
export interface DesignEnv {
  dc: string;
  links: { wallet: string; profile: string };
  vw: number;
  path: string;
  mapStyle: string;
  suppliers?: unknown;
  /** IMPL-010 — the live store catalogue (`window.__SG_STORE` shape) for the server-rendered Booking Store. */
  store?: unknown;
}

/** react-dom's namespace plus the client roots the runtime addresses as `ReactDOM.*`. */
const APP_REACT_DOM = { ...ReactDOMNamespace, createRoot, hydrateRoot };

function buildApp(env: DesignEnv) {
  const Logic = createDesignLogic(StreamableLogic, StreamableLogic, React, env);
  const app = createDesignApp({ React, ReactDOM: APP_REACT_DOM, ast: PATCHED_AST, Logic, dataProps: DESIGN_PROPS, designDocMode: DESIGN_DOC_MODE, sameSite: sameSiteHref });
  return { Root: app.Root as unknown as React.ComponentType, css: app.BASE_CSS + "\n" + DESIGN_PSEUDO_CSS };
}

export default function DesignBundle({ env, onMounted }: { env: DesignEnv; onMounted?: () => void }) {
  // One runtime + logic per component instance: per request on the server, once per mount in the browser.
  const [app] = React.useState(() => buildApp(env));
  React.useEffect(() => { onMounted?.(); }, [onMounted]);
  return (
    <div id="dc-root">
      <style>{app.css}</style>
      <app.Root />
    </div>
  );
}
